import React from 'react';
import { motion } from 'framer-motion';
import { ArrowRight } from 'lucide-react';
import { applicationJourney } from '../utils/mockData';
import { useLanguage } from '../contexts/LanguageContext';

const ApplicationJourneySection = () => {
  const { t, language } = useLanguage();

  return (
    <section id="journey" className="py-24 sm:py-32 bg-white">
      <div className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-12">
        {/* Header */}
        <div className="max-w-3xl mb-16">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="text-sm font-semibold text-eucalyptus-600 tracking-wider uppercase mb-4"
          >
            {t('The Process', '申请流程')}
          </motion.div>
          <motion.h2
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, delay: 0.1 }}
            className="text-4xl sm:text-5xl lg:text-6xl font-bold text-navy-900 mb-6 tracking-tight leading-tight"
          >
            {t('From application to care', '从申请到服务开始')}
            <br />
            <span className="text-eucalyptus-600">
              {t('— we walk with you', '— 我们全程陪伴')}
            </span>
          </motion.h2>
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, delay: 0.2 }}
            className="text-lg text-gray-600 leading-relaxed"
          >
            {t(
              'The journey typically takes 7–10 months. Our bilingual case managers guide you through every step, from your first phone call to the day care begins.',
              '整个流程通常需要7-10个月。我们的双语个案经理陪您走过每一步，从第一通电话到护理开始的那一天。'
            )}
          </motion.p>
        </div>

        {/* Journey Steps */}
        <div className="relative">
          {/* Vertical timeline line - desktop */}
          <div className="hidden lg:block absolute left-1/2 top-0 bottom-0 w-px bg-gradient-to-b from-eucalyptus-200 via-eucalyptus-400 to-eucalyptus-200 transform -translate-x-1/2" />

          <div className="space-y-8 lg:space-y-0">
            {applicationJourney.map((step, index) => {
              const isEven = index % 2 === 0;
              return (
                <motion.div
                  key={step.step}
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.6, delay: index * 0.1 }}
                  className={`lg:grid lg:grid-cols-2 lg:gap-16 relative ${
                    isEven ? '' : 'lg:direction-rtl'
                  }`}
                  data-testid={`journey-step-${step.step}`}
                >
                  {/* Step content */}
                  <div className={`${isEven ? 'lg:pr-16 lg:text-right' : 'lg:col-start-2 lg:pl-16'} lg:py-8`}>
                    <div className={`inline-flex items-center gap-3 mb-4 ${isEven ? 'lg:flex-row-reverse' : ''}`}>
                      <div className="w-10 h-10 rounded-full bg-eucalyptus-500 text-white flex items-center justify-center font-bold shadow-lg">
                        {step.step}
                      </div>
                      <span className="text-sm font-semibold text-eucalyptus-600 bg-eucalyptus-50 px-3 py-1 rounded-full">
                        {language === 'zh' && step.dayZh ? step.dayZh : step.day}
                      </span>
                    </div>
                    <h3 className="text-2xl sm:text-3xl font-bold text-navy-900 mb-3">
                      {language === 'zh' && step.titleZh ? step.titleZh : step.title}
                    </h3>
                    <p className="text-gray-600 leading-relaxed">
                      {language === 'zh' && step.descriptionZh ? step.descriptionZh : step.description}
                    </p>
                  </div>

                  {/* Center dot - desktop */}
                  <div className="hidden lg:block absolute left-1/2 top-16 w-4 h-4 bg-white border-4 border-eucalyptus-500 rounded-full transform -translate-x-1/2 z-10" />

                  {/* Empty spacer */}
                  <div className={isEven ? '' : 'lg:col-start-1 lg:row-start-1'} />
                </motion.div>
              );
            })}
          </div>
        </div>

        {/* Bottom note */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="mt-16 p-8 bg-navy-900 rounded-3xl text-white flex flex-col md:flex-row items-start md:items-center justify-between gap-6"
        >
          <div>
            <h3 className="text-xl sm:text-2xl font-bold mb-2">
              {t('Ready to start your journey?', '准备好开始了吗？')}
            </h3>
            <p className="text-gray-300">
              {t(
                'Our bilingual team helps you register with My Aged Care today — free.',
                '我们的双语团队今天就能协助您注册 My Aged Care — 完全免费。'
              )}
            </p>
          </div>
          <a
            href="#contact"
            className="flex-shrink-0 inline-flex items-center gap-2 px-6 py-4 bg-eucalyptus-500 hover:bg-eucalyptus-600 text-white rounded-full font-semibold transition-all"
          >
            {t('Start now', '现在开始')}
            <ArrowRight className="w-5 h-5" />
          </a>
        </motion.div>
      </div>
    </section>
  );
};

export default ApplicationJourneySection;
